/**
 * AASB 15 — Revenue from Contracts with Customers (lessee/seller)
 * Rulepack v1.0.0. Lessee-side / seller-side recognition checks.
 *
 * Out of scope (flagged but not validated): contract modifications, principal-vs-agent,
 * variable-consideration measurement (constraint applied flag honoured but not recalculated),
 * licences, warranties as separate POs.
 */
import { Finding, ValidationResult, DISCLAIMER } from './types';
import {
  CellValue, parseNumber, parseDate, parseBool, makeHeaderMap, normaliseHeader,
  acceptedHeadersFor, findingBuilder, severityCounts, compliantCount, fmt,
} from './_shared';

const RULEPACK_VERSION = 'aasb15-1.0.0';
const fb = findingBuilder('AASB_15', RULEPACK_VERSION);

const HMAP = makeHeaderMap([
  ['contract_id', 'contract id', 'contract no', 'contract number'],
  ['customer_id', 'customer id', 'customer'],
  ['revenue_type', 'revenue type', 'type'],
  ['recognition_method', 'recognition method', 'method'],
  ['contract_start', 'contract start', 'start date', 'start'],
  ['contract_end', 'contract end', 'end date', 'end'],
  ['contract_value', 'contract value', 'total value', 'tcv'],
  ['recognised_to_date', 'revenue recognised', 'recognised revenue', 'ytd recognised', 'recognised', 'recognised to date', 'recognised to-date', 'revenue to date'],
  ['performance_obligations', 'performance obligations', 'pos', 'po count'],
  ['variable_consideration', 'variable consideration', 'vc'],
  ['constraint_applied', 'constraint applied', 'constraint'],
  ['billing_to_date', 'billed to date', 'billing'],
  ['deferred_revenue', 'deferred revenue', 'unearned revenue'],
  ['as_at_date', 'as at', 'as at date', 'reporting date'],
]);

const REQUIRED = [
  'contract_id', 'recognition_method', 'contract_start', 'contract_end',
  'contract_value', 'recognised_to_date', 'performance_obligations',
];

function normaliseMethod(v: unknown): 'point_in_time' | 'over_time' | 'unknown' {
  const s = String(v ?? '').toLowerCase().trim().replace(/[\s-]+/g, '_');
  if (s.startsWith('point') || s === 'pit' || s === 'point_in_time') return 'point_in_time';
  if (s.startsWith('over') || s === 'ot' || s === 'over_time') return 'over_time';
  return 'unknown';
}

export function detectAASB15(headerRow: string[]): { confidence: number; matched: string[]; missing: string[] } {
  const hits = new Set<string>();
  for (const raw of headerRow) {
    const c = normaliseHeader(HMAP, String(raw ?? ''));
    if (c) hits.add(c);
  }
  const matched = REQUIRED.filter(r => hits.has(r));
  const missing = REQUIRED.filter(r => !hits.has(r));
  return { confidence: matched.length / REQUIRED.length, matched, missing };
}

export function validateAASB15(
  headerRow: string[],
  dataRows: CellValue[][],
  defaultAsAtDate: Date = new Date()
): ValidationResult {
  const findings: Finding[] = [];
  const schemaErrors: string[] = [];

  const idx: Record<string, number> = {};
  headerRow.forEach((raw, i) => {
    const c = normaliseHeader(HMAP, String(raw ?? ''));
    if (c) idx[c] = i;
  });

  for (const f of REQUIRED) {
    if (idx[f] === undefined) {
      schemaErrors.push(`Missing required column "${f}". Accepted: ${acceptedHeadersFor(HMAP, f).join(', ')}.`);
    }
  }
  if (schemaErrors.length) {
    return {
      summary: { standard: 'AASB_15', rulepackVersion: RULEPACK_VERSION, rowsTested: 0, compliant: 0, byseverity: { info: 0, warning: 0, error: 0 }, runAt: new Date().toISOString() },
      findings, schemaErrors, disclaimer: DISCLAIMER,
    };
  }

  const rowIds: string[] = [];

  dataRows.forEach((raw, i) => {
    const rowNum = i + 2;
    const get = (k: string) => raw[idx[k]];
    const id = String(get('contract_id') ?? `row-${rowNum}`);
    rowIds.push(id);

    // R01 completeness
    const missing = REQUIRED.filter(f => {
      const v = get(f);
      return v == null || v === '';
    });
    if (missing.length) {
      findings.push(fb.build('AASB15.R01', 'error', 'Required field missing',
        `Row ${rowNum} (${id}): missing ${missing.join(', ')}.`,
        { rowId: id, evidence: { missing } }));
      return;
    }

    const value = parseNumber(get('contract_value'));
    const recognised = parseNumber(get('recognised_to_date'));
    const startD = parseDate(get('contract_start'));
    const endD = parseDate(get('contract_end'));
    const method = normaliseMethod(get('recognition_method'));
    const pos = parseNumber(get('performance_obligations'));
    const asAt = idx.as_at_date !== undefined ? (parseDate(get('as_at_date')) ?? defaultAsAtDate) : defaultAsAtDate;
    const billing = idx.billing_to_date !== undefined ? parseNumber(get('billing_to_date')) : null;
    const deferred = idx.deferred_revenue !== undefined ? parseNumber(get('deferred_revenue')) : null;
    const vc = idx.variable_consideration !== undefined ? parseNumber(get('variable_consideration')) : null;
    const constraintApplied = idx.constraint_applied !== undefined ? parseBool(get('constraint_applied')) : null;

    // R02 contract value > 0
    if (!(value !== null && value > 0)) {
      findings.push(fb.build('AASB15.R02', 'error', 'Contract value must be positive', `Contract ${id}: value ${value}.`, { rowId: id }));
      return;
    }
    // R03 recognised >= 0
    if (!(recognised !== null && recognised >= 0)) {
      findings.push(fb.build('AASB15.R03', 'error', 'Recognised-to-date must be non-negative', `Contract ${id}: $${fmt(recognised ?? 0)}.`, { rowId: id }));
      return;
    }
    // R05 method recognised
    if (method === 'unknown') {
      findings.push(fb.build('AASB15.R05', 'warning', 'Recognition method not recognised',
        `Contract ${id}: method ${get('recognition_method')}. Use point_in_time or over_time.`,
        { rowId: id }));
      return;
    }
    // R06 dates
    if (startD && endD && endD < startD) {
      findings.push(fb.build('AASB15.R06', 'error', 'Contract end before start',
        `Contract ${id}: end ${endD.toISOString().slice(0, 10)} < start ${startD.toISOString().slice(0, 10)}.`,
        { rowId: id }));
      return;
    }
    // R09 POs
    if (!(pos !== null && pos >= 1)) {
      findings.push(fb.build('AASB15.R09', 'warning', 'Contract should have at least one performance obligation',
        `Contract ${id}: PO count ${pos}.`,
        { rowId: id }));
    }

    const tol = Math.max(50, 0.005 * value);

    // R04 over-recognition
    if (recognised > value + tol) {
      findings.push(fb.build('AASB15.R04', 'error', 'Recognised-to-date exceeds contract value',
        `Contract ${id}: recognised $${fmt(recognised)} > value $${fmt(value)} (delta +$${fmt(recognised - value)}).`,
        { rowId: id, evidence: { recognised, value }, suggestedAdjustment: { field: 'recognised_to_date', from: recognised, to: value, rationale: 'Cap at contract value.' } }));
    }

    // R07 / R08 — recognition pattern by method
    if (method === 'point_in_time') {
      if (recognised > 0 && recognised < value - tol) {
        findings.push(fb.build('AASB15.R07', 'warning', 'Point-in-time partial recognition',
          `Contract ${id}: point-in-time methods recognise either $0 (not delivered) or $${fmt(value)} (fully delivered). Reported $${fmt(recognised)} suggests over-time treatment may be appropriate, or the contract has multiple delivery points each with its own PO.`,
          { rowId: id, evidence: { recognised, value, method } }));
      }
    } else if (method === 'over_time' && startD && endD) {
      const totalDays = (endD.getTime() - startD.getTime()) / 86400000;
      const elapsedDays = Math.max(0, Math.min(totalDays, (asAt.getTime() - startD.getTime()) / 86400000));
      const expected = totalDays > 0 ? value * (elapsedDays / totalDays) : 0;
      const recTol = Math.max(tol, 0.10 * value);
      if (Math.abs(recognised - expected) > recTol) {
        const sev: Finding['severity'] = Math.abs(recognised - expected) > 0.25 * value ? 'error' : 'warning';
        findings.push(fb.build('AASB15.R08', sev, 'Over-time recognition differs from time-elapsed proxy',
          `Contract ${id}: recognised $${fmt(recognised)}, time-elapsed proxy $${fmt(expected)} (${(elapsedDays / totalDays * 100).toFixed(1)}% elapsed). If output- or input-based measure differs from straight-line, document the basis.`,
          { rowId: id, evidence: { recognised, expected, elapsedPct: elapsedDays / totalDays } }));
      }
    }

    // R10 variable consideration constraint
    if (vc !== null && vc > 0 && constraintApplied === false) {
      findings.push(fb.build('AASB15.R10', 'warning', 'Variable consideration constraint not applied',
        `Contract ${id}: variable consideration $${fmt(vc)} present but constraint flag is false. AASB 15.56 requires constraining VC to amounts highly probable not to result in a significant reversal.`,
        { rowId: id, evidence: { vc } }));
    }

    // R11 deferred revenue reasonableness
    if (billing !== null && deferred !== null) {
      const expectedDeferred = Math.max(0, billing - recognised);
      const dTol = Math.max(50, 0.01 * value);
      if (Math.abs(deferred - expectedDeferred) > dTol) {
        findings.push(fb.build('AASB15.R11', 'warning', 'Deferred revenue does not reconcile to billing − recognised',
          `Contract ${id}: deferred $${fmt(deferred)}, expected $${fmt(expectedDeferred)} (billing − recognised).`,
          { rowId: id, evidence: { deferred, expected: expectedDeferred, billing, recognised } }));
      }
    }

    // R12 contract ended but not recognised
    if (endD && endD < asAt && recognised < value - tol) {
      findings.push(fb.build('AASB15.R12', 'warning', 'Contract end passed but not fully recognised',
        `Contract ${id}: ended ${endD.toISOString().slice(0, 10)}; reported $${fmt(recognised)} < value $${fmt(value)}. Confirm cancellations or scope reductions are reflected.`,
        { rowId: id, evidence: { recognised, value, endDate: endD.toISOString() } }));
    }
  });

  return {
    summary: {
      standard: 'AASB_15',
      rulepackVersion: RULEPACK_VERSION,
      rowsTested: rowIds.length,
      compliant: compliantCount(rowIds, findings),
      byseverity: severityCounts(findings),
      runAt: new Date().toISOString(),
    },
    findings,
    disclaimer: DISCLAIMER,
  };
}
